import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import {
  LUXPLUS_PROMO_BANNER_CTA_LINK_TYPES,
  LUXPLUS_PROMO_BANNER_CTA_LINK_TYPE_LABELS,
} from '~/utils/luxPlusPayloadBuilder/constants';
import { createDefaultPromoBanner } from '~/utils/luxPlusPayloadBuilder/promoBannerCarousel/defaults';
import type {
  LuxPlusPromoBanner,
  LuxPlusPromoBannerCtaLinkType,
} from '~/utils/luxPlusPayloadBuilder/promoBannerCarousel/types';
import { formatPromoTitle, getNextPromoDraftLabel, movePromo } from '~/utils/luxPlusPayloadBuilder/promos';

import {
  PAYLOAD_BUILDER_TITLE_WARNING_LENGTH,
  PayloadBuilderTextField,
  getPayloadBuilderCopyLengthWarningProps,
} from '../../FormFields';
import PayloadBuilderRegionChipsField from '../../RegionChipsField';
import { SectionCard } from '../../SectionCard';

interface PromoBannerCarouselBannersSectionProps {
  promos: Array<LuxPlusPromoBanner>;
  onChange: (promos: Array<LuxPlusPromoBanner>) => void;
}

function updatePromo(
  promos: Array<LuxPlusPromoBanner>,
  index: number,
  patch: Partial<LuxPlusPromoBanner>,
): Array<LuxPlusPromoBanner> {
  return promos.map((promo, promoIndex) => (promoIndex === index ? { ...promo, ...patch } : promo));
}

interface PromoBannerEditorProps {
  promo: LuxPlusPromoBanner;
  index: number;
  promos: Array<LuxPlusPromoBanner>;
  onChange: (promos: Array<LuxPlusPromoBanner>) => void;
}

function PromoBannerEditor({ promo, index, promos, onChange }: PromoBannerEditorProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const title = formatPromoTitle(promo.draftLabel, promo.title);

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, expanded ? 0.08 : 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
        }}
      >
        <Button
          variant="text"
          color="primary"
          onClick={() => setExpanded((value) => !value)}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
              }}
            />
          }
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            px: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          <Typography variant="subtitle2" component="span" noWrap>
            {title}
          </Typography>
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Chip label={`Position ${index + 1}`} size="small" color="primary" variant="outlined" />
          <Tooltip title="Move promo up">
            <Box component="span">
              <IconButton
                aria-label={`Move promo ${index + 1} up`}
                disabled={index === 0}
                onClick={() => onChange(movePromo(promos, index, -1))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Move promo down">
            <Box component="span">
              <IconButton
                aria-label={`Move promo ${index + 1} down`}
                disabled={index === promos.length - 1}
                onClick={() => onChange(movePromo(promos, index, 1))}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Remove promo">
            <IconButton
              aria-label={`Remove promo ${index + 1}`}
              onClick={() => onChange(promos.filter((_, promoIndex) => promoIndex !== index))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                select
                label="CTA link type"
                value={promo.ctaLinkType}
                onChange={(event) =>
                  onChange(
                    updatePromo(promos, index, { ctaLinkType: event.target.value as LuxPlusPromoBannerCtaLinkType }),
                  )
                }
                helperText="Sign up opens the LuxPlus+ join flow. Link uses the CTA URL."
                required
              >
                {LUXPLUS_PROMO_BANNER_CTA_LINK_TYPES.map((ctaLinkType) => (
                  <MenuItem key={ctaLinkType} value={ctaLinkType}>
                    {LUXPLUS_PROMO_BANNER_CTA_LINK_TYPE_LABELS[ctaLinkType]}
                  </MenuItem>
                ))}
              </PayloadBuilderTextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="Expires at"
                type="datetime-local"
                value={promo.expiresAt}
                onChange={(event) => onChange(updatePromo(promos, index, { expiresAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Optional. Configured in Australia/Sydney. Leave empty for no expiry."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderRegionChipsField
                label="Eligible Regions"
                helperText="Customer portal only shows this banner in selected regions. At least one region is required."
                regions={promo.eligibleRegions}
                onChange={(eligibleRegions) => onChange(updatePromo(promos, index, { eligibleRegions }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="Label"
                value={promo.label}
                onChange={(event) => onChange(updatePromo(promos, index, { label: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="CTA text"
                value={promo.ctaText}
                onChange={(event) => onChange(updatePromo(promos, index, { ctaText: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label="Title"
                value={promo.title}
                onChange={(event) => onChange(updatePromo(promos, index, { title: event.target.value }))}
                required
                {...getPayloadBuilderCopyLengthWarningProps(promo.title, PAYLOAD_BUILDER_TITLE_WARNING_LENGTH)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label="CTA URL"
                value={promo.ctaLink}
                onChange={(event) => onChange(updatePromo(promos, index, { ctaLink: event.target.value }))}
                helperText="Required even for Sign up. Use an https:// URL or an internal route starting with /"
                required
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function PromoBannerCarouselBannersSection({
  promos,
  onChange,
}: PromoBannerCarouselBannersSectionProps) {
  return (
    <SectionCard
      title="Promo banners"
      subtitle="Banners shown on the LuxPlus+ landing and account pages."
      count={promos.length}
    >
      <Stack spacing={2}>
        {promos.map((promo, index) => (
          <PromoBannerEditor key={promo.draftId} promo={promo} index={index} promos={promos} onChange={onChange} />
        ))}
        <Button
          variant="outlined"
          onClick={() => onChange([...promos, createDefaultPromoBanner(getNextPromoDraftLabel(promos))])}
        >
          Add promo banner
        </Button>
      </Stack>
    </SectionCard>
  );
}
